import {parse, walk, generate, clone} from 'css-tree';
import type {FunctionNode, Value} from 'css-tree';

/**
 * Intentionally over-permissive. Correctness comes from the css-tree parser
 * downstream — the regex only filters obviously-irrelevant inputs.
 */
const HAS_VIEWPORT_UNIT = /(?:^|[^a-z_-])(?:s|d|l)?v(?:h|w|min|max|i|b)\b/i;
const HAS_OPEN_CANVAS_VAR = /--__wf_oc_(?:[sdl]?v(?:h|w|min|max|i|b))\b/i;
const HAS_ROOT_SELECTOR = /:root\b/i;
const OPEN_CANVAS_FRAME_CONTENT_SELECTOR = '[data-wf-frame-id] > *';

const UNIT_TO_VAR: Record<string, string> = {
  vh: '--__wf_oc_vh',
  svh: '--__wf_oc_svh',
  dvh: '--__wf_oc_dvh',
  lvh: '--__wf_oc_lvh',
  vw: '--__wf_oc_vw',
  svw: '--__wf_oc_svw',
  dvw: '--__wf_oc_dvw',
  lvw: '--__wf_oc_lvw',
  vmin: '--__wf_oc_vmin',
  svmin: '--__wf_oc_svmin',
  dvmin: '--__wf_oc_dvmin',
  lvmin: '--__wf_oc_lvmin',
  vmax: '--__wf_oc_vmax',
  svmax: '--__wf_oc_svmax',
  dvmax: '--__wf_oc_dvmax',
  lvmax: '--__wf_oc_lvmax',
  vi: '--__wf_oc_vi',
  svi: '--__wf_oc_svi',
  dvi: '--__wf_oc_dvi',
  lvi: '--__wf_oc_lvi',
  vb: '--__wf_oc_vb',
  svb: '--__wf_oc_svb',
  dvb: '--__wf_oc_dvb',
  lvb: '--__wf_oc_lvb',
};

const VAR_TO_UNIT: Record<string, string> = {
  '--__wf_oc_vh': 'vh',
  '--__wf_oc_svh': 'svh',
  '--__wf_oc_dvh': 'dvh',
  '--__wf_oc_lvh': 'lvh',
  '--__wf_oc_vw': 'vw',
  '--__wf_oc_svw': 'svw',
  '--__wf_oc_dvw': 'dvw',
  '--__wf_oc_lvw': 'lvw',
  '--__wf_oc_vmin': 'vmin',
  '--__wf_oc_svmin': 'svmin',
  '--__wf_oc_dvmin': 'dvmin',
  '--__wf_oc_lvmin': 'lvmin',
  '--__wf_oc_vmax': 'vmax',
  '--__wf_oc_svmax': 'svmax',
  '--__wf_oc_dvmax': 'dvmax',
  '--__wf_oc_lvmax': 'lvmax',
  '--__wf_oc_vi': 'vi',
  '--__wf_oc_svi': 'svi',
  '--__wf_oc_dvi': 'dvi',
  '--__wf_oc_lvi': 'lvi',
  '--__wf_oc_vb': 'vb',
  '--__wf_oc_svb': 'svb',
  '--__wf_oc_dvb': 'dvb',
  '--__wf_oc_lvb': 'lvb',
};

const REWRITTEN_VIEWPORT_VALUE_PATTERN =
  /^calc\(var\((--__wf_oc_[sdl]?v(?:h|w|min|max|i|b))\)\*(-?\d*\.?\d+)\)$/;

/**
 * Templates are parsed once at module load and cloned per match.
 * Hand-constructing css-tree nodes is error-prone (List vs array, required
 * WhiteSpace/Operator tokens inside calc, casing rules). Do not "optimize"
 * this back to manual node construction.
 *
 * The shape of the parsed AST (Value root, Function as first child) is
 * pinned by the `__TEST_ONLY_TEMPLATE_BY_VAR__` test, so a css-tree change
 * that broke either cast would fail in CI before it could fail at runtime.
 */
function parseTemplate(varName: string): FunctionNode {
  const ast = parse(`calc(var(${varName}) * 0)`, {context: 'value'});
  return (ast as Value).children.first as FunctionNode;
}

const TEMPLATE_BY_VAR: Map<string, FunctionNode> = new Map();
for (const varName of new Set(Object.values(UNIT_TO_VAR))) {
  TEMPLATE_BY_VAR.set(varName, parseTemplate(varName));
}

/**
 * Test-only export. Do not use outside tests — `TEMPLATE_BY_VAR` is
 * implementation detail and may change shape without notice.
 */
export const __TEST_ONLY_TEMPLATE_BY_VAR__ = TEMPLATE_BY_VAR;

function buildReplacement(unit: string, value: string): FunctionNode | null {
  const varName = UNIT_TO_VAR[unit.toLowerCase()];
  if (varName == null) return null;

  const template = TEMPLATE_BY_VAR.get(varName);
  if (template == null) return null;

  const cloned = clone(template) as FunctionNode;

  walk(cloned, {
    visit: 'Number',
    enter(numberNode) {
      numberNode.value = value;
    },
  });

  return cloned;
}

/**
 * Rewrites viewport-relative length units (vh/vw/vmin/vmax/vi/vb and the s/d/l
 * variants) inside a CSS value or declaration list, replacing each one with
 * a calc(var(--__wf_oc_*) * N) expression that resolves against the current
 * Open Canvas frame.
 */
export function rewriteViewportUnits(
  input: string,
  context: 'value' | 'declarationList'
): string {
  if (!HAS_VIEWPORT_UNIT.test(input)) return input;

  try {
    let modified = false;
    const ast = parse(input, {context});

    walk(ast, {
      visit: 'Dimension',
      enter(node, item, list) {
        if (item == null || list == null) return;
        const replacement = buildReplacement(node.unit, node.value);
        if (replacement == null) return;
        list.replace(item, list.createItem(replacement));
        modified = true;
      },
    });

    // Raw nodes wrap unparsed value fragments (var() fallbacks, custom-property
    // values without parseCustomProperty). Delegate text-substitution back into
    // ourselves so the rewriting logic stays in one place.
    walk(ast, {
      visit: 'Raw',
      enter(node) {
        if (!HAS_VIEWPORT_UNIT.test(node.value)) return;
        const rewritten = rewriteViewportUnits(node.value, 'value');
        if (rewritten === node.value) return;
        node.value = rewritten;
        modified = true;
      },
    });

    if (!modified) return input;
    return generate(ast);
  } catch (_error) {
    return input;
  }
}

/**
 * Rewrites viewport-relative length units inside a full CSS stylesheet
 * (rules, at-rules, grouping rules). Delegates each declaration's value to
 * rewriteViewportUnits(value, 'value') so the unit-mapping logic lives in
 * exactly one place.
 */
export function rewriteViewportUnitsInCssText(cssText: string): string {
  if (!HAS_VIEWPORT_UNIT.test(cssText)) return cssText;

  try {
    let modified = false;
    const ast = parse(cssText, {
      context: 'stylesheet',
      parseCustomProperty: true,
    });

    walk(ast, {
      visit: 'Declaration',
      enter(declaration) {
        const valueText = generate(declaration.value);
        const rewritten = rewriteViewportUnits(valueText, 'value');
        if (rewritten === valueText) return;

        try {
          const rewrittenAst = parse(rewritten, {context: 'value'});
          if (rewrittenAst.type === 'Value') {
            declaration.value = rewrittenAst;
            modified = true;
          }
        } catch (_error) {
          // Leave declaration unchanged on re-parse failure.
        }
      },
    });

    if (!modified) return cssText;
    return generate(ast);
  } catch (_error) {
    return cssText;
  }
}

/**
 * Rewrites `:root` selectors in customer-authored stylesheets so custom
 * properties are declared on each Open Canvas frame's content root.
 */
export function rewriteRootSelectorsInCssText(cssText: string): string {
  if (!HAS_ROOT_SELECTOR.test(cssText)) return cssText;

  try {
    let modified = false;
    const ast = parse(cssText, {context: 'stylesheet'});
    const targetList = parse(OPEN_CANVAS_FRAME_CONTENT_SELECTOR, {
      context: 'selectorList',
    });

    if (targetList.type !== 'SelectorList') return cssText;

    const targetSelector = targetList.children.first;

    if (targetSelector == null || targetSelector.type !== 'Selector') {
      return cssText;
    }

    walk(ast, {
      visit: 'PseudoClassSelector',
      enter(node, item, list) {
        if (
          node.name.toLowerCase() !== 'root' ||
          item == null ||
          list == null
        ) {
          return;
        }

        const clonedTarget = clone(targetSelector);
        if (clonedTarget.type !== 'Selector') return;

        list.replace(item, clonedTarget.children);
        modified = true;
      },
    });

    if (!modified) return cssText;
    return generate(ast);
  } catch (_error) {
    return cssText;
  }
}

/**
 * Rewrites customer-authored style tag CSS so it resolves against the active
 * Open Canvas frame instead of the iframe document.
 */
export function rewriteStyleTagCssForOpenCanvas(cssText: string): string {
  return rewriteRootSelectorsInCssText(rewriteViewportUnitsInCssText(cssText));
}

function reverseRewrittenViewportUnit(node: FunctionNode): string | null {
  const serialized = generate(node);
  const matched = serialized.match(REWRITTEN_VIEWPORT_VALUE_PATTERN);
  if (matched == null) return null;

  const varName = matched[1];
  const numericValue = matched[2];
  if (varName == null || numericValue == null) return null;

  const unit = VAR_TO_UNIT[varName];
  if (unit == null) return null;

  return `${numericValue}${unit}`;
}

/**
 * Reverses `calc(var(--__wf_oc_*) * N)` values back to viewport units.
 * Used by CSS preview to revert Open Canvas-specific rewrites.
 */
export function reverseRewriteViewportUnitsInCssText(cssText: string): string {
  if (!HAS_OPEN_CANVAS_VAR.test(cssText)) return cssText;

  try {
    let modified = false;
    const ast = parse(cssText, {
      context: 'stylesheet',
      parseCustomProperty: true,
    });

    walk(ast, {
      visit: 'Function',
      enter(node, item, list) {
        if (item == null || list == null || node.name !== 'calc') return;

        const dimension = reverseRewrittenViewportUnit(node);
        if (dimension == null) return;

        try {
          const parsed = parse(dimension, {context: 'value'}) as Value;
          const replacement = parsed.children.first;
          if (replacement == null) return;

          list.replace(item, list.createItem(replacement));
          modified = true;
        } catch (_error) {
          // Leave function unchanged on re-parse failure.
        }
      },
    });

    if (!modified) return cssText;
    return generate(ast);
  } catch (_error) {
    return cssText;
  }
}
